import {css} from "@panda/css";

import {IIcon, MapIcon, PhoneIcon, SmartSearchIcon, TilesIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {HOTLINE_INFO_PHONE_NUMBER} from "../../hotline/constants/hotline_phone_numbers";
import {selectSemanticSearchHasConversation} from "../../semantic_search/state/select_semantic_search_has_conversation";
import {openSemanticSearchHalfSheet, openSemanticSearchModal} from "../../semantic_search/state/semantic_search_modal_slice";
import {hitAiAssistant} from "../../semantic_search/tracking/semantic_search_gtm_events";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {trackAmplitudeNavigationClick} from "../tracking/track_amplitude_navigation_click";

const BOTTOM_BAR_SECTION_NAME = "rp_listing_bottom_bar";

interface IProps {
    isMap: boolean;
    listUrl?: string;
    mapUrl?: string;
}

export const MobileListingBottomBar = ({isMap, listUrl, mapUrl}: IProps) => {
    const dispatch = useAppDispatch();
    const hasConversation = useAppSelector(selectSemanticSearchHasConversation);

    const handleSemanticSearchOpen = () => {
        trackAmplitudeNavigationClick(BOTTOM_BAR_SECTION_NAME, undefined, "Asystent AI");
        hitAiAssistant("open");
        dispatch(hasConversation ? openSemanticSearchModal() : openSemanticSearchHalfSheet());
    };

    const handleMapListToggleClick = () => {
        trackAmplitudeNavigationClick(BOTTOM_BAR_SECTION_NAME, undefined, isMap ? "Lista" : "Mapa");
    };

    const handleHotlineClick = () => {
        trackAmplitudeNavigationClick(BOTTOM_BAR_SECTION_NAME, undefined, "Infolinia");
    };

    return (
        <div className={wrapperStyle}>
            <div className={barShadowStyle}>
                <div className={barStyle}>
                    <a href={isMap ? listUrl : mapUrl} className={sideItemStyle} onClick={handleMapListToggleClick}>
                        {isMap ? <TilesIcon {...iconProps} /> : <MapIcon {...iconProps} />}
                        <Text variant="info_txt_2" as="span">
                            {isMap ? "Lista" : "Mapa"}
                        </Text>
                    </a>

                    <div className={notchSpacerStyle} aria-hidden="true" />

                    <a href={`tel:+${HOTLINE_INFO_PHONE_NUMBER.replace(/ /g, "")}`} className={sideItemStyle} onClick={handleHotlineClick}>
                        <PhoneIcon {...iconProps} />
                        <Text variant="info_txt_2" as="span">
                            Infolinia
                        </Text>
                    </a>
                </div>
            </div>

            <button type="button" className={aiButtonStyle} onClick={handleSemanticSearchOpen}>
                <SmartSearchIcon {...iconProps} fill="var(--colors-white)" />
                <Text variant="info_txt_2" as="span" color="var(--colors-white)">
                    Asystent AI
                </Text>
            </button>
        </div>
    );
};

const iconProps: IIcon = {
    size: "2.4",
    wrapperSize: "2.4",
    wrapperColor: "transparent"
};

const wrapperStyle = css({
    position: "fixed",
    bottom: 0,
    left: 0,
    zIndex: 21,
    width: "100%",
    paddingBottom: "calc(var(--spacing-1) + env(safe-area-inset-bottom, 0px))",
    px: 0.5,

    md: {
        display: "none"
    }
});

const barShadowStyle = css({
    borderRadius: "16px",
    boxShadow: `0px -4px 24px 0px rgba(0, 0, 0, 0.08)`
});

const barStyle = css({
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    justifyItems: "center",
    width: "100%",
    height: "55px",
    backgroundColor: "var(--colors-white)",

    clipPath:
        "polygon(0px 16px, 0.78px 11.06px, 3.06px 6.6px, 6.6px 3.06px, 11.06px 0.78px, 16px 0px, calc(50% - 53.969px) 0px, calc(50% - 52.096px) 0.231px, calc(50% - 50.253px) 0.891px, calc(50% - 48.509px) 1.917px, calc(50% - 46.931px) 3.241px, calc(50% - 45.586px) 4.799px, calc(50% - 44.541px) 6.526px, calc(50% - 43.865px) 8.357px, calc(50% - 43.625px) 10.226px, calc(50% - 43.4px) 13.715px, calc(50% - 42.739px) 17.104px, calc(50% - 41.664px) 20.374px, calc(50% - 40.197px) 23.509px, calc(50% - 38.36px) 26.492px, calc(50% - 36.174px) 29.306px, calc(50% - 33.663px) 31.933px, calc(50% - 30.847px) 34.356px, calc(50% - 27.749px) 36.559px, calc(50% - 24.391px) 38.523px, calc(50% - 20.794px) 40.233px, calc(50% - 16.981px) 41.67px, calc(50% - 12.973px) 42.817px, calc(50% - 8.792px) 43.658px, calc(50% - 4.46px) 44.175px, 50% 44.352px, calc(50% + 4.46px) 44.175px, calc(50% + 8.792px) 43.658px, calc(50% + 12.973px) 42.817px, calc(50% + 16.981px) 41.67px, calc(50% + 20.794px) 40.233px, calc(50% + 24.391px) 38.523px, calc(50% + 27.749px) 36.559px, calc(50% + 30.847px) 34.356px, calc(50% + 33.663px) 31.933px, calc(50% + 36.174px) 29.306px, calc(50% + 38.36px) 26.492px, calc(50% + 40.197px) 23.509px, calc(50% + 41.664px) 20.374px, calc(50% + 42.739px) 17.104px, calc(50% + 43.4px) 13.715px, calc(50% + 43.625px) 10.226px, calc(50% + 43.865px) 8.357px, calc(50% + 44.541px) 6.526px, calc(50% + 45.586px) 4.799px, calc(50% + 46.931px) 3.241px, calc(50% + 48.509px) 1.917px, calc(50% + 50.253px) 0.891px, calc(50% + 52.096px) 0.231px, calc(50% + 53.969px) 0px, calc(100% - 16px) 0px, calc(100% - 11.06px) 0.78px, calc(100% - 6.6px) 3.06px, calc(100% - 3.06px) 6.6px, calc(100% - 0.78px) 11.06px, 100% 16px, 100% 39px, calc(100% - 0.78px) 43.94px, calc(100% - 3.06px) 48.4px, calc(100% - 6.6px) 51.94px, calc(100% - 11.06px) 54.22px, calc(100% - 16px) 55px, 16px 55px, 11.06px 54.22px, 6.6px 51.94px, 3.06px 48.4px, 0.78px 43.94px, 0px 39px)"
});

const notchSpacerStyle = css({
    width: "108px"
});

const sideItemStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0.5,
    textDecoration: "none",
    color: "var(--colors-secondary)",
    width: "100%"
});

const aiButtonStyle = css({
    position: "absolute",
    left: "50%",
    top: 0,
    width: "72px",
    height: "72px",
    transform: "translate(-50%, -45%)",
    borderRadius: "50%",
    background: "var(--colors-secondary)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    border: "none",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none"
});
import {css} from "@panda/css";

import {IIcon, MapIcon, PhoneIcon, SmartSearchIcon, TilesIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {HOTLINE_INFO_PHONE_NUMBER} from "../../hotline/constants/hotline_phone_numbers";
import {selectSemanticSearchHasConversation} from "../../semantic_search/state/select_semantic_search_has_conversation";
import {openSemanticSearchHalfSheet, openSemanticSearchModal} from "../../semantic_search/state/semantic_search_modal_slice";
import {hitAiAssistant} from "../../semantic_search/tracking/semantic_search_gtm_events";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {trackAmplitudeNavigationClick} from "../tracking/track_amplitude_navigation_click";

const BOTTOM_BAR_SECTION_NAME = "rp_listing_bottom_bar";

interface IProps {
    isMap: boolean;
    listUrl?: string;
    mapUrl?: string;
}

export const MobileListingBottomBar = ({isMap, listUrl, mapUrl}: IProps) => {
    const dispatch = useAppDispatch();
    const hasConversation = useAppSelector(selectSemanticSearchHasConversation);

    const handleSemanticSearchOpen = () => {
        trackAmplitudeNavigationClick(BOTTOM_BAR_SECTION_NAME, undefined, "Asystent AI");
        hitAiAssistant("open");
        dispatch(hasConversation ? openSemanticSearchModal() : openSemanticSearchHalfSheet());
    };

    const handleMapListToggleClick = () => {
        trackAmplitudeNavigationClick(BOTTOM_BAR_SECTION_NAME, undefined, isMap ? "Lista" : "Mapa");
    };

    const handleHotlineClick = () => {
        trackAmplitudeNavigationClick(BOTTOM_BAR_SECTION_NAME, undefined, "Infolinia");
    };

    return (
        <div className={wrapperStyle}>
            <div className={barShadowStyle}>
                <div className={barStyle}>
                    <a href={isMap ? listUrl : mapUrl} className={sideItemStyle} onClick={handleMapListToggleClick}>
                        {isMap ? <TilesIcon {...iconProps} /> : <MapIcon {...iconProps} />}
                        <Text variant="info_txt_2" as="span">
                            {isMap ? "Lista" : "Mapa"}
                        </Text>
                    </a>

                    <div className={notchSpacerStyle} aria-hidden="true" />

                    <a href={`tel:+${HOTLINE_INFO_PHONE_NUMBER.replace(/ /g, "")}`} className={sideItemStyle} onClick={handleHotlineClick}>
                        <PhoneIcon {...iconProps} />
                        <Text variant="info_txt_2" as="span">
                            Infolinia
                        </Text>
                    </a>
                </div>
            </div>

            <button type="button" className={aiButtonStyle} onClick={handleSemanticSearchOpen}>
                <SmartSearchIcon {...iconProps} fill="var(--colors-white)" />
                <Text variant="info_txt_2" as="span" color="var(--colors-white)">
                    Asystent AI
                </Text>
            </button>
        </div>
    );
};

const iconProps: IIcon = {
    size: "2.4",
    wrapperSize: "2.4",
    wrapperColor: "transparent"
};

const wrapperStyle = css({
    position: "fixed",
    bottom: 0,
    left: 0,
    zIndex: 21,
    width: "100%",
    paddingBottom: "calc(var(--spacing-1) + env(safe-area-inset-bottom, 0px))",
    px: 0.5,

    md: {
        display: "none"
    }
});

const barShadowStyle = css({
    borderRadius: "16px",
    boxShadow: `0px -4px 24px 0px rgba(0, 0, 0, 0.08)`
});

const barStyle = css({
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    justifyItems: "center",
    width: "100%",
    height: "55px",
    backgroundColor: "var(--colors-white)",

    clipPath:
        "polygon(0px 16px, 0.78px 11.06px, 3.06px 6.6px, 6.6px 3.06px, 11.06px 0.78px, 16px 0px, calc(50% - 53.969px) 0px, calc(50% - 52.096px) 0.231px, calc(50% - 50.253px) 0.891px, calc(50% - 48.509px) 1.917px, calc(50% - 46.931px) 3.241px, calc(50% - 45.586px) 4.799px, calc(50% - 44.541px) 6.526px, calc(50% - 43.865px) 8.357px, calc(50% - 43.625px) 10.226px, calc(50% - 43.4px) 13.715px, calc(50% - 42.739px) 17.104px, calc(50% - 41.664px) 20.374px, calc(50% - 40.197px) 23.509px, calc(50% - 38.36px) 26.492px, calc(50% - 36.174px) 29.306px, calc(50% - 33.663px) 31.933px, calc(50% - 30.847px) 34.356px, calc(50% - 27.749px) 36.559px, calc(50% - 24.391px) 38.523px, calc(50% - 20.794px) 40.233px, calc(50% - 16.981px) 41.67px, calc(50% - 12.973px) 42.817px, calc(50% - 8.792px) 43.658px, calc(50% - 4.46px) 44.175px, 50% 44.352px, calc(50% + 4.46px) 44.175px, calc(50% + 8.792px) 43.658px, calc(50% + 12.973px) 42.817px, calc(50% + 16.981px) 41.67px, calc(50% + 20.794px) 40.233px, calc(50% + 24.391px) 38.523px, calc(50% + 27.749px) 36.559px, calc(50% + 30.847px) 34.356px, calc(50% + 33.663px) 31.933px, calc(50% + 36.174px) 29.306px, calc(50% + 38.36px) 26.492px, calc(50% + 40.197px) 23.509px, calc(50% + 41.664px) 20.374px, calc(50% + 42.739px) 17.104px, calc(50% + 43.4px) 13.715px, calc(50% + 43.625px) 10.226px, calc(50% + 43.865px) 8.357px, calc(50% + 44.541px) 6.526px, calc(50% + 45.586px) 4.799px, calc(50% + 46.931px) 3.241px, calc(50% + 48.509px) 1.917px, calc(50% + 50.253px) 0.891px, calc(50% + 52.096px) 0.231px, calc(50% + 53.969px) 0px, calc(100% - 16px) 0px, calc(100% - 11.06px) 0.78px, calc(100% - 6.6px) 3.06px, calc(100% - 3.06px) 6.6px, calc(100% - 0.78px) 11.06px, 100% 16px, 100% 39px, calc(100% - 0.78px) 43.94px, calc(100% - 3.06px) 48.4px, calc(100% - 6.6px) 51.94px, calc(100% - 11.06px) 54.22px, calc(100% - 16px) 55px, 16px 55px, 11.06px 54.22px, 6.6px 51.94px, 3.06px 48.4px, 0.78px 43.94px, 0px 39px)"
});

const notchSpacerStyle = css({
    width: "108px"
});

const sideItemStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0.5,
    textDecoration: "none",
    color: "var(--colors-secondary)",
    width: "100%"
});

const aiButtonStyle = css({
    position: "absolute",
    left: "50%",
    top: 0,
    width: "72px",
    height: "72px",
    transform: "translate(-50%, -45%)",
    borderRadius: "50%",
    background: "var(--colors-secondary)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    border: "none",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none"
});
